import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';

/** Decodes HTML entities (e.g. &#39; → ') stored in notification text fields. */
const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import NotificationDotAtom, { NotificationPriority } from '../../atoms/NotificationDot/NotificationDotAtom';
import NotificationTimestampAtom from '../../atoms/NotificationTimestamp/NotificationTimestampAtom';
import SyndicationRevokedNotificationCardMolecule, {
  SyndicationRevokedNotificationMetadata,
} from '../notifications/SyndicationRevokedNotificationCardMolecule/SyndicationRevokedNotificationCardMolecule';
import './NotificationCardMolecule.scss';

export type KnownNotificationType =
  | 'sphere-created'
  | 'conversation-notification'
  | 'sphere-status-alert'
  | 'sphere-check-in'
  | 'syndication-revoked';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- intentional {}-tagged string to preserve literal autocomplete while accepting arbitrary backend values
export type NotificationType = KnownNotificationType | (string & {});

export interface NotificationRecord {
  id: string;
  user_id: string;
  sphere_id?: string;
  center_id?: string;
  type: NotificationType;
  title: string;
  content: string;
  summary?: string;
  metadata?: any;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  priority?: NotificationPriority;
  created_at: string;
  read_at?: string;
}

export interface NotificationTypeConfig {
  value: string;
  label: string;
  icon: string;
  color?: string; // Optional: not used for display, reserved for future use
}

export interface NotificationCardMoleculeProps {
  notification: NotificationRecord;
  typeConfig?: NotificationTypeConfig;
  onClick?: (notification: NotificationRecord) => void;
  onMarkRead?: (notificationId: string) => void;
  onViewSphere?: (sphereId: string) => void;
  className?: string;
}

/**
 * NotificationCardMolecule - Individual notification card
 * Based on SphereChatPanel card design with NotificationBell styling
 * Supports expand/collapse for viewing full notification summary
 */
const NotificationCardMolecule: React.FC<NotificationCardMoleculeProps> = ({ notification, typeConfig, onClick, onMarkRead, onViewSphere, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (notification.type === 'syndication-revoked') {
    return (
      <SyndicationRevokedNotificationCardMolecule
        metadata={notification.metadata as SyndicationRevokedNotificationMetadata}
        onNavigate={onClick ? () => onClick(notification) : undefined}
        className={className}
      />
    );
  }

  const isUnread = notification.status !== 'read';
  const priority = notification.priority || 'normal';
  const hasSummary = notification.summary && notification.summary.trim().length > 0;

  const handleCardClick = () => {
    if (onClick) {
      onClick(notification);
    }
  };

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkRead) {
      onMarkRead(notification.id);
    }
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleToggleKeyboard = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    }
  };

  const cardHeader = (
    <div className="notification-card-header">
      <div className="notification-card-title-section">
        <NotificationDotAtom isUnread={isUnread} priority={priority} />
        {typeConfig && <i className={`pi ${typeConfig.icon} notification-type-icon`}></i>}
        <span className="notification-card-title">{notification.title}</span>
      </div>
      <NotificationTimestampAtom timestamp={notification.created_at} />
    </div>
  );

  return (
    <Card
      className={`notification-card-molecule ${isUnread ? 'unread' : 'read'} ${isExpanded ? 'expanded' : 'collapsed'} ${className}`}
      onClick={handleCardClick}
      header={cardHeader}
    >
      <div className="notification-card-content">
        <p>{decodeHtmlEntities(notification.content)}</p>
      </div>

      {/* Expandable summary section */}
      {hasSummary && (
        <div className={`notification-card-summary ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="notification-card-summary-content" dangerouslySetInnerHTML={{ __html: notification.summary || '' }} />
        </div>
      )}

      <div className="notification-card-footer">
        <div className="notification-card-meta">
          {priority !== 'normal' && <Chip label={priority.toUpperCase()} className={`notification-priority-chip priority-${priority}`} />}

          {/* Expand/Collapse toggle button */}
          {hasSummary && (
            <button
              className="notification-expand-toggle"
              onClick={handleToggleExpand}
              onKeyDown={handleToggleKeyboard}
              aria-label={isExpanded ? 'Collapse notification summary' : 'Expand notification summary'}
              aria-expanded={isExpanded}
              tabIndex={0}
            >
              <i className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
              <span>{isExpanded ? 'Show less' : 'Show more'}</span>
            </button>
          )}
        </div>

        <div className="notification-card-actions">
          {isExpanded && notification.sphere_id && onViewSphere && (
            <Button
              label="View Sphere"
              icon="pi pi-arrow-right"
              iconPos="right"
              text
              size="small"
              className="notification-view-sphere-btn"
              onClick={(e) => {
                e.stopPropagation();
                onViewSphere(notification.sphere_id!);
              }}
            />
          )}
          {isUnread && onMarkRead && (
            <Button label="Mark as read" icon="pi pi-check" text size="small" onClick={handleMarkRead} className="notification-mark-read-btn" />
          )}
        </div>
      </div>
    </Card>
  );
};

export default NotificationCardMolecule;
